// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { ImageProps } from 'next/image'
import type { TFunction } from 'react-i18next'
import type { OutwriteShadesBrandColor } from '@outwrite/themes'
import type { I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import { useInView } from 'react-intersection-observer'
import styled from '@emotion/styled'
import { TertiaryLinkButton } from '@outwrite/button'

// Others
import useScrollPassed from '../hooks/use-scrolled-passed'
import persistQueriesLink from '../hocs/persist-queries-link'
import withDefault from '../util/with-default'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'

import {
  HeroSection,
  Title as HeroTitle,
  Description as HeroDescription
} from '../components/Hero'
import { StatDivider, StatNumbers, StatText, StatWrapper } from '../components/Stats'
import { CenteredSection, SpacingContainer, Title } from '../components/CenteredSection'

import PrimaryAction from '../components/PrimaryAction'
import Highlight from '../components/Highlight'
import BottomCta from '../components/BottomCta'
import FiveStars from '../components/FiveStars'

import {
  TABLET_WIDTH_PX,
  CHROME_EXT_LINKS_BY_LOCALE,
  defaultPageSpecificSEO,
  pageSpecificCtaLink,
  MOBILE_WIDTH_PX,
  CAPTERRA_LINKS_BY_LOCALE
} from '../constants'

import ChromeImg from '../../public/assets/reviewers/chrome.png'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'reviews' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, reviews: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & styles
 */

const Footnote = styled.p`
  ${(props): string => props.theme.outwrite.typos.default.normal[500]}
  color: ${(props): string => props.theme.outwrite.colors.gray[700]};
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${(props): string => props.theme.outwrite.typos.default.normal[200]}
  }
`
const ColumnWrapper = styled(SpacingContainer)`
  display: flex;
  flex-direction: row;
  @media screen and (max-width: ${TABLET_WIDTH_PX}px) {
    flex-direction: column;
  }
  text-align: left;
`
const Column = styled.div`
  margin: 0 32px;
  flex: 1 1 0%;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    margin: 0 8px;
  }
`

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`

const ReviewerContainer = styled.div`
  display: flex;
  padding: 16px 0;
  margin-bottom: 48px;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    margin-bottom: 32px;
  }
`

const AuthorContainer = styled.div`
  margin-left: 16px;
  color: ${(props): string => props.theme.outwrite.colors.black};
`

const AuthorName = styled.p`
  margin: 0 0 4px 0;
  ${(props): string => props.theme.outwrite.typos.default['semi-bold'][400]}
`

const AuthorDescription = styled.p`
  margin: 0;
  ${(props): string => props.theme.outwrite.typos.default.normal[300]}
`

interface QuoteContainerProps {
  readonly 'data-bgcolor'?: keyof OutwriteShadesBrandColor
}

const QuoteContainer = styled.div<QuoteContainerProps>`
  padding: 48px;
  border-radius: ${(props): number => props.theme.outwrite.borderRadii.small}px;
  background-color: ${(props): string => props['data-bgcolor']
      ? props.theme.outwrite.colors[props['data-bgcolor']][200]
      : props.theme.outwrite.colors.white
  };
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    padding: 24px;
  }
`

const EmphasisQuote = styled.q`
 ${(props): string => props.theme.outwrite.typos.default['semi-bold'][700]}
 color: ${(props): string => props.theme.outwrite.colors.gray[900]};

 @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
  ${(props): string => props.theme.outwrite.typos.default['semi-bold'][500]}
 }
`
const Quote = styled.p`
 ${(props): string => props.theme.outwrite.typos.default.normal[500]}
 color: ${(props): string => props.theme.outwrite.colors.gray[700]};

 @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
  ${(props): string => props.theme.outwrite.typos.default.normal[300]}
 }
`

const TwitterTag = styled.span`
  color: rgb(29, 155, 240);
`

const StarContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > * {
    margin-right: 8px;
  }
`

const Date = styled.p`
  ${(props): string => props.theme.outwrite.typos.default.normal[200]};
  color: ${(props): string => props.theme.outwrite.colors.gray[900]};
  white-space: nowrap;
  @media screen and (max-width: ${MOBILE_WIDTH_PX}px) {
    ${(props): string => props.theme.outwrite.typos.default.normal[100]}
  }
`

const LinkAction = persistQueriesLink(styled(TertiaryLinkButton)`
  display: flex;
  flex-direction: column;
  width: 100%;
`)

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type ReviewsKey = I18nextKey<typeof import ('../../public/locales/en/reviews.json')>
interface Review {
  quote: React.FC<{ t: TFunction<'reviews', undefined> }>
  author: Record<'name' | 'description', ReviewsKey> & { img: ImageProps }
}

const IN_VIEW_OPTIONS = { triggerOnce: true, initialInView: false, threshold: 0.2 }
const ReviewComponent: React.FC<{ review: Review, t: TFunction<'reviews', undefined> }> = ({ review, t }) => {
  const { ref, inView } = useInView(IN_VIEW_OPTIONS)
  return (
    <ReviewContainer
      ref={ref}
      style={{
        transform: inView ? 'translateY(0)' : 'translateY(200px)',
        opacity: inView ? 1 : 0,
        transition: 'transform .5s cubic-bezier(0, 0, 0.2, 1), opacity .5s'
      }}
    >
      <review.quote t={t} />
      <ReviewerContainer>
        <Image alt='' {...review.author.img} />
        <AuthorContainer>
          <AuthorName>{t(review.author.name)}</AuthorName>
          <AuthorDescription>{t(review.author.description)}</AuthorDescription>
        </AuthorContainer>
      </ReviewerContainer>
    </ReviewContainer>
  )
}

const REVIEWS: Review[] = [
  {
    author: {
      name: 'reviews.quotes.ANDY_TWOMEY.name',
      description: 'reviews.quotes.ANDY_TWOMEY.description',
      img: { src: require('../../public/assets/reviewers/andy-twomey.png'), alt: '', height: 64, width: 64 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer data-bgcolor='purple'>
        <EmphasisQuote>{t('reviews.quotes.ANDY_TWOMEY.quote.title')}</EmphasisQuote>
        <Quote>{t('reviews.quotes.ANDY_TWOMEY.quote.description')}</Quote>
      </QuoteContainer>
    )
  },
  {
    author: {
      name: 'reviews.quotes.STEFFANY_LOO.name',
      description: 'reviews.quotes.STEFFANY_LOO.description',
      img: { src: ChromeImg, alt: '', height: 60, width: 60 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer>
        <Quote>{t('reviews.quotes.STEFFANY_LOO.quote.description')}</Quote>
        <StarContainer>
          <FiveStars />
          <Date>{t('reviews.quotes.STEFFANY_LOO.quote.date')}</Date>
        </StarContainer>
      </QuoteContainer>
    )
  },
  {
    author: {
      name: 'reviews.quotes.SHANTELLE_ISAAKS.name',
      description: 'reviews.quotes.SHANTELLE_ISAAKS.description',
      img: { src: require('../../public/assets/reviewers/shantelle-isaaks.png'), alt: '', height: 60, width: 60 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer data-bgcolor='yellow'>
        <EmphasisQuote>{t('reviews.quotes.SHANTELLE_ISAAKS.quote.title')}</EmphasisQuote>
        <Quote>{t('reviews.quotes.SHANTELLE_ISAAKS.quote.description')}</Quote>
      </QuoteContainer>
    )
  },
  {
    author: {
      name: 'reviews.quotes.ABHISHEK_PURI.name',
      description: 'reviews.quotes.ABHISHEK_PURI.description',
      img: { src: require('../../public/assets/reviewers/twitter.png'), alt: '', height: 50, width: 50 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer>
        <Quote>
          <Trans
            t={t}
            i18nKey={'reviews.quotes.ABHISHEK_PURI.quote.description'}
            components={[<TwitterTag key={0} />]}
          />
        </Quote>
      </QuoteContainer>
    )
  },
  {
    author: {
      name: 'reviews.quotes.DEB_CARR.name',
      description: 'reviews.quotes.DEB_CARR.description',
      img: { src: require('../../public/assets/reviewers/deb-carr.png'), alt: '', height: 64, width: 64 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer data-bgcolor='red'>
        <EmphasisQuote>{t('reviews.quotes.DEB_CARR.quote.title')}</EmphasisQuote>
        <Quote>{t('reviews.quotes.DEB_CARR.quote.description')}</Quote>
      </QuoteContainer>
    )
  },
  {
    author: {
      name: 'reviews.quotes.CHRIS_BAKER.name',
      description: 'reviews.quotes.CHRIS_BAKER.description',
      img: { src: ChromeImg, alt: '', height: 60, width: 60 }
    },
    quote: ({ t }): JSX.Element => (
      <QuoteContainer>
        <Quote>{t('reviews.quotes.CHRIS_BAKER.quote.description')}</Quote>
        <StarContainer>
          <FiveStars />
          <Date>{t('reviews.quotes.CHRIS_BAKER.quote.date')}</Date>
        </StarContainer>
      </QuoteContainer>
    )
  }
]

const STATS: Array<React.FC<{ t: TFunction<'reviews', undefined>, locale?: string }>> = [
  ({ t, locale }): JSX.Element => (
    <LinkAction {...withDefault(CHROME_EXT_LINKS_BY_LOCALE, locale)}>
      <StatNumbers color='purple'>{t('ratings.CHROME.rating')}</StatNumbers>
      <StatText>{t('ratings.CHROME.rater')}</StatText>
    </LinkAction>
  ),
  ({ t, locale }): JSX.Element => (
    <LinkAction {...withDefault(CAPTERRA_LINKS_BY_LOCALE, locale)}>
      <StatNumbers color='red'>{t('ratings.CAPTERRA.rating')}</StatNumbers>
      <StatText>{t('ratings.CAPTERRA.rater')}</StatText>
    </LinkAction>
  ),
  ({ t }): JSX.Element => (
    <LinkAction exthref='https://www.g2.com/products/outwrite-for-teams/reviews'>
      <StatNumbers color='blue'>{t('ratings.G2.rating')}</StatNumbers>
      <StatText>{t('ratings.G2.rater')}</StatText>
    </LinkAction>
  )
]

const Reviews: NextPage = () => {
  const router = useRouter()
  const { t } = useTranslation('reviews')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = {
    ...t('CTA_BUTTON'),
    ...pageSpecificCtaLink(router.pathname, router.locale)
  }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      <Header t={tCommon} action={ctaAction} data-sticky={scrolledPassed} />

      {/* Hero */}
      <HeroSection>
        <HeroTitle>
          <Trans
            t={t}
            i18nKey={'hero.TITLE'}
            components={[<Highlight color='purple' key={0} />, <Highlight color='yellow' key={1} />]}
          />
        </HeroTitle>
        <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
        <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
        <Footnote>{t('hero.FOOT_NOTE')}</Footnote>
      </HeroSection>

      <CenteredSection bgColor='gray'>
        <SpacingContainer>
          <Title>{t('reviews.TITLE')}</Title>
        </SpacingContainer>
        <SpacingContainer>
          <ColumnWrapper>
            <Column>
              {REVIEWS.slice(0, REVIEWS.length / 2)
                .map(r => <ReviewComponent review={r} t={t} key={r.author.name} />)}
            </Column>
            <Column>
              {REVIEWS.slice(REVIEWS.length / 2)
                .map(r => <ReviewComponent review={r} t={t} key={r.author.name} />)}
            </Column>
          </ColumnWrapper>
        </SpacingContainer>
      </CenteredSection>

      <CenteredSection>
        <Title>{t('ratings.TITLE')}</Title>
        <StatWrapper>
          {STATS.map((Component, i) => (
            <StatDivider key={i}>
              <Component t={t} locale={router.locale} />
            </StatDivider>
          ))}
        </StatWrapper>
      </CenteredSection>

      {/* BottomCta */}
      <BottomCta
        action={ctaAction}
        {...t('BOTTOM_CTA')}
        bgColor='gray'
      />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Reviews
